import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatInt, formatPercent } from "@/lib/statsFormat";
import type { CallStatsDurationBuckets } from "@/services/callStatsService";
import ChannelToggle, { type ChannelFilter } from "./ChannelToggle";

const LABELS = ["Under 10 s", "10–30 s", "30–60 s", "1–3 min", "3–10 min", "Over 10 min"];

interface CallLengthChartProps {
  rows: CallStatsDurationBuckets[];
}

const CallLengthChart = ({ rows }: CallLengthChartProps) => {
  const [channel, setChannel] = useState<ChannelFilter>("all");

  const buckets = useMemo(
    () =>
      LABELS.map((_, index) =>
        rows
          .filter((row) => channel === "all" || row.channel === channel)
          .reduce((acc, row) => acc + (row.buckets[index] ?? 0), 0),
      ),
    [rows, channel],
  );
  const total = buckets.reduce((acc, count) => acc + count, 0);
  const max = Math.max(...buckets);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Call length</CardTitle>
        <ChannelToggle value={channel} onChange={setChannel} />
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No connected calls of this kind in this range
          </p>
        ) : (
          <div className="space-y-3">
            {LABELS.map((label, index) => (
              <div key={label} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>{label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {formatInt(buckets[index])} ·{" "}
                    {formatPercent((buckets[index] / total) * 100)}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-chart-1"
                    style={{ width: `${(buckets[index] / max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CallLengthChart;
